import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Shuffle, Copy, Loader2 } from "lucide-react";

const genPassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  const arr = new Uint32Array(12);
  window.crypto.getRandomValues(arr);
  return Array.from(arr, (n) => chars[n % chars.length]).join("");
};

// target: { type: "partner" | "cliente", id, name, email }
export default function ResetPasswordDialog({ target, onClose }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  useEffect(() => { setPw(""); setDone(null); }, [target]);

  const save = async () => {
    if (pw && pw.length < 8) return toast.error("La password deve contenere almeno 8 caratteri");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-password", { target: target.type, id: target.id, password: pw || null });
      setDone(data); toast.success("Password aggiornata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const copy = () => navigator.clipboard?.writeText(done.password).then(() => toast.success("Password copiata"));

  return (
    <Dialog open={!!target} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="reset-password-dialog">
        <DialogHeader><DialogTitle>Cambia password · {target?.name}</DialogTitle></DialogHeader>
        {done ? (
          <div className="space-y-3 text-sm" data-testid="reset-password-done">
            <div>Nuova password impostata per <strong>{done.email}</strong>. Le sessioni aperte sono state chiuse.</div>
            <div className="flex items-center gap-2 rounded-lg bg-slate-50 border p-3">
              <span className="font-mono font-bold flex-1 break-all" data-testid="reset-password-value">{done.password}</span>
              <Button variant="ghost" size="icon" onClick={copy} data-testid="reset-password-copy"><Copy className="w-4 h-4" /></Button>
            </div>
            <div className="text-xs text-slate-500">Comunica la password all'utente: non verrà più mostrata.</div>
          </div>
        ) : (
          <div className="space-y-2">
            <Label className="text-xs">Nuova password (min 8 caratteri) · lascia vuoto per generarla</Label>
            <div className="flex gap-2">
              <Input value={pw} onChange={(e) => setPw(e.target.value)} className="font-mono" data-testid="reset-password-input" />
              <Button type="button" variant="outline" onClick={() => setPw(genPassword())} data-testid="reset-password-generate"><Shuffle className="w-4 h-4 mr-1" /> Genera</Button>
            </div>
            <div className="text-xs text-slate-500">Account: {target?.email}</div>
          </div>
        )}
        <DialogFooter>
          {done ? <Button onClick={onClose} data-testid="reset-password-close">Chiudi</Button> : <>
            <Button variant="outline" onClick={onClose}>Annulla</Button>
            <Button onClick={save} disabled={busy} className="bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="reset-password-save">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Salva password
            </Button>
          </>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
